"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";

import type { JudgeProblemCredit } from "../types";

/**
 * `EX-ARC01`, `docs/29` §6.4: where a practice problem came from.
 *
 * A round's problems reach the archive by one operator act, and the page says
 * so: which round. Nothing here is stored on the problem; the round comes from
 * the contest's mount rows, and a private round is never named.
 *
 * Who wrote it is said once, under the title (`ProblemHead`, `EX-CRD01`,
 * ADR-0098), so this line keeps the round and no longer names the person.
 *
 * `lang` is the statement panel's own toggle rather than the page locale, so
 * the round's title follows the language the reader is actually reading in.
 */
export function ArchiveCredit({
  credit,
  lang,
  locale,
}: {
  credit: JudgeProblemCredit | null;
  lang: "en" | "bn";
  locale: string;
}) {
  const t = useTranslations("judge.problem");
  if (!credit) return null;

  const { round } = credit;
  if (!round) return null;

  const roundTitle = (lang === "bn" ? round.title_bn : null) ?? round.title_en;

  return (
    <div className="mt-6 rounded-xl border border-[rgb(var(--border))] bg-neutral-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[rgb(var(--muted-foreground))]">
        {t("creditHeading")}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-neutral-800">
        {/*
          The name is an ICU value inside the tag, not a chunk supplied here, so
          each locale puts the round and the verb wherever its own word order
          wants them ("Set for X" / "X এর জন্য বানানো").
        */}
        <span>
          {t.rich("creditFromRound", {
            name: roundTitle,
            round: (chunks) => (
              <Link
                href={`/${locale}/contests/${round.slug}`}
                className="font-semibold text-neutral-900 underline decoration-neutral-300 underline-offset-2 transition-colors hover:decoration-neutral-900"
              >
                {chunks}
              </Link>
            ),
          })}
        </span>
      </div>
    </div>
  );
}
